import { Box, Portal, useTheme, useMediaQuery } from "@mui/material";
import { Transcript } from "../../../types/realtimeSpeech";
import { CustomTypography } from "../../custom-typography/custom-typography";
import ReactMarkdown from "react-markdown";
import { usePortalContainer } from "../../../PortalContext";
import { ArrowForwardIcon, ArrowDownIcon } from "../../../assets/icon";
import { getTranscriptContainerStyles, transcriptStyles } from "./styles";
import { ReactElement } from "react";

interface TranscriptPanelProps {
  transcript: Transcript | null;
  onHide: () => void;
}

export const TranscriptPanel = ({
  transcript,
  onHide,
}: TranscriptPanelProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const containerRef = usePortalContainer();

  if (!containerRef.current) return null;

  const getTranscriptContent = (transcript: Transcript | null) => {
    if (!transcript) return null;

    // Inline markdown components for rendering markdown within spans
    const inlineMarkdownComponents = {
      p: ({ children }: any) => <span>{children}</span>,
      strong: ({ children }: any) => <strong>{children}</strong>,
      em: ({ children }: any) => <em>{children}</em>,
    };

    let transcriptPanelContent: ReactElement[] = [];

    transcriptPanelContent = Object.entries(transcript).map(([key, value]) => {
      const text =
        value && typeof value === "object" && typeof value.text === "string"
          ? value.text
          : "Not provided";
      const highlighted_part =
        value &&
        typeof value === "object" &&
        typeof value.highlighted_part === "string" &&
        value.highlighted_part.trim()
          ? value.highlighted_part
          : null;
      const commentary =
        value &&
        typeof value === "object" &&
        typeof value.commentary === "string" &&
        value.commentary.trim()
          ? value.commentary
          : null;

      // Check if text starts with "TAP -" or "HCP -" (with or without markdown)
      const tapMatch = text.match(/^(\*\*)?TAP(\*\*)?\s*-\s*(.*)$/);
      const hcpMatch = text.match(/^(\*\*)?HCP(\*\*)?\s*-\s*(.*)$/);
      const speaker = tapMatch ? "TAP" : hcpMatch ? "HCP" : null;
      const dialogueText = tapMatch
        ? tapMatch[3]
        : hcpMatch
          ? hcpMatch[3]
          : text;

      const highlighted_part_index =
        highlighted_part && dialogueText !== "Not provided"
          ? dialogueText.indexOf(highlighted_part)
          : -1;

      let content;

      if (highlighted_part_index !== -1) {
        const beforeHighlight = dialogueText.slice(0, highlighted_part_index);
        const highlightText = dialogueText.slice(
          highlighted_part_index,
          highlighted_part_index + (highlighted_part?.length ?? 0)
        );
        const afterHighlight = dialogueText.slice(
          highlighted_part_index + (highlighted_part?.length ?? 0)
        );

        content = (
          <span
            key={key}
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "flex-start",
              margin: "10px 0",
            }}
          >
            <span>
              {key} s -{" "}
              {speaker ? (
                <>
                  <strong>{speaker}</strong> -{" "}
                  <ReactMarkdown components={inlineMarkdownComponents}>
                    {beforeHighlight}
                  </ReactMarkdown>
                  <span style={{ backgroundColor: "yellow", color: "black" }}>
                    <ReactMarkdown components={inlineMarkdownComponents}>
                      {highlightText}
                    </ReactMarkdown>
                  </span>
                  <ReactMarkdown components={inlineMarkdownComponents}>
                    {afterHighlight}
                  </ReactMarkdown>
                </>
              ) : (
                <>
                  <ReactMarkdown components={inlineMarkdownComponents}>
                    {beforeHighlight}
                  </ReactMarkdown>
                  <span style={{ backgroundColor: "yellow", color: "black" }}>
                    <ReactMarkdown components={inlineMarkdownComponents}>
                      {highlightText}
                    </ReactMarkdown>
                  </span>
                  <ReactMarkdown components={inlineMarkdownComponents}>
                    {afterHighlight}
                  </ReactMarkdown>
                </>
              )}
            </span>
            {commentary && (
              <span
                style={{
                  width: "fit-content",
                  display: "inline-block",
                  padding: "8px",
                  marginTop: "5px",
                  borderRadius: "8px",
                  borderTopLeftRadius: 0,
                  backgroundColor: "#ebebff",
                  color: "black",
                }}
              >
                <ReactMarkdown components={inlineMarkdownComponents}>
                  {commentary}
                </ReactMarkdown>
              </span>
            )}
          </span>
        );
      } else {
        content = (
          <span
            key={key}
            style={{
              display: "inline-flex",
              flexDirection: "column",
              margin: "10px 0",
            }}
          >
            <span>
              {key} s -{" "}
              {speaker ? (
                <>
                  <strong>{speaker}</strong> -{" "}
                  <ReactMarkdown components={inlineMarkdownComponents}>
                    {dialogueText}
                  </ReactMarkdown>
                </>
              ) : (
                <ReactMarkdown components={inlineMarkdownComponents}>
                  {dialogueText}
                </ReactMarkdown>
              )}
            </span>
            {commentary && (
              <span
                style={{
                  width: "fit-content",
                  display: "inline-block",
                  padding: "8px",
                  marginTop: "5px",
                  borderRadius: "8px",
                  borderTopLeftRadius: 0,
                  backgroundColor: "#ebebff",
                  color: "black",
                }}
              >
                <ReactMarkdown components={inlineMarkdownComponents}>
                  {commentary}
                </ReactMarkdown>
              </span>
            )}
            <br key={`br-${text}`} />
          </span>
        );
      }

      return content;
    });

    return <>{transcriptPanelContent}</>;
  };

  return (
    <Portal container={containerRef.current}>
      <Box sx={getTranscriptContainerStyles(Boolean(transcript))}>
        <button
          onClick={onHide}
          style={{
            width: "25px",
            height: "50px",
            border: "none",
            cursor: "pointer",
            zIndex: 3,
            alignSelf: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSmall ? (
            <ArrowDownIcon fill={theme.palette.primary.main} />
          ) : (
            <ArrowForwardIcon fill={theme.palette.primary.main} />
          )}
        </button>
        <CustomTypography
          variant="body2"
          color={isDark ? "common.white" : "text.primary"}
          sx={transcriptStyles}
        >
          {getTranscriptContent(transcript)}
        </CustomTypography>
      </Box>
    </Portal>
  );
};
